import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as api from '../utils/apiCalls';
import { setActiveBoxId } from '../utils/apiCalls';
import { useAuth } from './AuthContext';
import type { Box, BoxLocation, BoxLocationPaginationResponse, BoxPaginationResponse } from '../../types/models';

interface BoxContextValue {
  boxes: Box[];
  selectedBox: Box | null;
  selectedBoxId: string | null;
  selectBox: (boxId: string) => void;
  boxLocations: BoxLocation[];
  selectedBoxLocation: BoxLocation | null;
  selectedBoxLocationId: string | null;
  selectBoxLocation: (locationId: string) => void;
  loading: boolean;
  refetchBoxes: () => void;
}

const BoxContext = createContext<BoxContextValue | undefined>(undefined);

export const BoxProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [selectedBoxId, setSelectedBoxId] = useState<string | null>(null);
  const [boxLocations, setBoxLocations] = useState<BoxLocation[]>([]);
  const [selectedBoxLocationId, setSelectedBoxLocationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const clearLocationState = useCallback(() => {
    setBoxLocations([]);
    setSelectedBoxLocationId(null);
    localStorage.removeItem('selectedBoxLocationId');
  }, []);

  const fetchBoxes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<BoxPaginationResponse>('/boxes');
      const list = res.list || [];
      setBoxes(list);

      const savedBoxId = localStorage.getItem('selectedBoxId');
      if (savedBoxId && list.some((b) => b.id === savedBoxId)) {
        setSelectedBoxId(savedBoxId);
        setActiveBoxId(savedBoxId);
      } else if (list.length === 1) {
        const autoId = list[0].id!;
        setSelectedBoxId(autoId);
        setActiveBoxId(autoId);
        localStorage.setItem('selectedBoxId', autoId);
      } else {
        setSelectedBoxId(null);
        setActiveBoxId(null);
        localStorage.removeItem('selectedBoxId');
        clearLocationState();
      }
    } catch {
      setBoxes([]);
      setSelectedBoxId(null);
      setActiveBoxId(null);
      clearLocationState();
    } finally {
      setLoading(false);
    }
  }, [clearLocationState]);

  useEffect(() => {
    if (!isAuthenticated) {
      setBoxes([]);
      setSelectedBoxId(null);
      setActiveBoxId(null);
      localStorage.removeItem('selectedBoxId');
      clearLocationState();
      return;
    }
    fetchBoxes();
  }, [isAuthenticated, fetchBoxes, clearLocationState]);

  useEffect(() => {
    if (!selectedBoxId) {
      clearLocationState();
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<BoxLocationPaginationResponse>('/box-locations');
        if (cancelled) return;
        const locs = res.list || [];
        setBoxLocations(locs);

        const savedLocId = localStorage.getItem('selectedBoxLocationId');
        if (savedLocId && locs.some((l) => l.id === savedLocId)) {
          setSelectedBoxLocationId(savedLocId);
          return;
        }

        if (locs.length === 1) {
          const autoId = locs[0].id!;
          setSelectedBoxLocationId(autoId);
          localStorage.setItem('selectedBoxLocationId', autoId);
          return;
        }

        setSelectedBoxLocationId(null);
        localStorage.removeItem('selectedBoxLocationId');
      } catch {
        if (!cancelled) clearLocationState();
      }
    })();

    return () => { cancelled = true; };
  }, [selectedBoxId, clearLocationState]);

  const selectBox = useCallback(
    (boxId: string) => {
      if (!boxes.some((b) => b.id === boxId)) return;
      setSelectedBoxId(boxId);
      setActiveBoxId(boxId);
      localStorage.setItem('selectedBoxId', boxId);
      clearLocationState();
    },
    [boxes, clearLocationState],
  );

  const selectBoxLocation = useCallback(
    (locationId: string) => {
      if (!boxLocations.some((l) => l.id === locationId)) return;
      setSelectedBoxLocationId(locationId);
      localStorage.setItem('selectedBoxLocationId', locationId);
    },
    [boxLocations],
  );

  const selectedBox = useMemo(
    () => boxes.find((b) => b.id === selectedBoxId) ?? null,
    [boxes, selectedBoxId],
  );

  const selectedBoxLocation = useMemo(
    () => boxLocations.find((l) => l.id === selectedBoxLocationId) ?? null,
    [boxLocations, selectedBoxLocationId],
  );

  const value = useMemo(
    () => ({
      boxes,
      selectedBox,
      selectedBoxId,
      selectBox,
      boxLocations,
      selectedBoxLocation,
      selectedBoxLocationId,
      selectBoxLocation,
      loading,
      refetchBoxes: fetchBoxes,
    }),
    [boxes, selectedBox, selectedBoxId, selectBox, boxLocations, selectedBoxLocation, selectedBoxLocationId, selectBoxLocation, loading, fetchBoxes],
  );

  return <BoxContext.Provider value={value}>{children}</BoxContext.Provider>;
};

export function useBox(): BoxContextValue {
  const ctx = useContext(BoxContext);
  if (!ctx) {
    throw new Error('useBox must be used within BoxProvider');
  }
  return ctx;
}
